import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, Sun, Moon, Save, Check, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';

/* ─── Toast ─── */
export function ToastContainer({ toasts }) {
  return (
    <div className="toast-container" role="status" aria-live="polite" aria-atomic="false">
      {toasts.map(t => (
        <div key={t.id} className="toast">
          <span>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Modal ─── */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({ title, onClose, children, footer, wide = false, variant }) {
  const dialogRef = useRef(null);

  // Trap focus inside the modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE) || []);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }, [onClose]);

  // Move focus into dialog on open
  useEffect(() => {
    const prev = document.activeElement;
    const first = dialogRef.current?.querySelector(FOCUSABLE);
    first?.focus();
    return () => prev?.focus();
  }, []);

  const isSheet = variant === 'sheet';
  const cls = `modal${wide ? ' modal-wide' : ''}${isSheet ? ' modal-sheet' : ''}`;

  return (
    <div
      className={`modal-backdrop${isSheet ? ' modal-backdrop-sheet' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        className={cls}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={handleKeyDown}
      >
        {isSheet && <div className="modal-sheet-grabber" aria-hidden="true" />}
        <div className="modal-header-row">
          <h3 className="modal-title" style={{ margin: 0 }}>{title}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Dismiss dialog"><X size={14} /></button>
        </div>
        <div className={isSheet ? 'modal-sheet-body' : undefined}>
          {children}
        </div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ─── Confirm Dialog ─── */
export function ConfirmDialog({ title, message, onConfirm, onCancel, danger }) {
  return (
    <Modal title={title} onClose={onCancel} footer={
      <>
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>Confirm</button>
      </>
    }>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{message}</p>
    </Modal>
  );
}

/* ─── Progress Bar ─── */
export function ProgressBar({ value, max, color = 'var(--savings)' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="progress-wrap">
      <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/* ─── Empty State ─── */
export function EmptyState({ icon, title, sub, action }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      <div className="empty-state-title">{title}</div>
      {sub && <div className="empty-state-sub">{sub}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

/* ─── Budget/savings row icon ────────────────────────────────────────────────
 * Shows the element's emoji, or a semi-transparent dot in the "relevant" color
 * (income/expense/savings) when none is set. When `onClick` is given it's a
 * button that opens an icon picker (stops propagation so it doesn't trigger the
 * surrounding row's tap). Used in the budget category / yearly / savings rows.
 */
export function BudgetIcon({ emoji, color, onClick, desktop = false }) {
  const inner = emoji
    ? emoji
    : <span className="budget-dot" style={{ background: `color-mix(in srgb, ${color} 42%, transparent)` }} />;
  if (!onClick) return <span className={`budget-icon${desktop ? ' budget-icon-desktop' : ''}`}>{inner}</span>;
  return (
    <button
      type="button"
      className={`budget-icon budget-icon-btn${desktop ? ' budget-icon-desktop' : ''}`}
      title="Set icon"
      aria-label="Set icon"
      onClick={e => { e.stopPropagation(); onClick(); }}
    >
      {inner}
    </button>
  );
}

/* ─── Emoji picker modal ─────────────────────────────────────────────────────
 * Standard searchable emoji picker (native style — offline, no CDN images).
 * `onPick(emoji)` selects, `onClear()` removes the current icon.
 */
export function EmojiPickerModal({ current, theme, onPick, onClear, onClose }) {
  return (
    <Modal title="Choose icon" onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Close</button>}>
      {current && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 8 }} onClick={onClear}>
          <Trash2 size={12} /> Remove icon
        </button>
      )}
      <EmojiPicker
        onEmojiClick={e => onPick(e.emoji)}
        emojiStyle={EmojiStyle.NATIVE}
        theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
        width="100%"
        height={340}
        lazyLoadEmojis
        previewConfig={{ showPreview: false }}
      />
    </Modal>
  );
}

/* ─── List Row (mobile card list) ───────────────────────────────────────────
 * Touch-friendly row used in place of table rows on phones.
 *   leading  – emoji / icon / colored dot (rendered in a circular chip)
 *   title    – primary text (truncated)
 *   sub      – muted secondary line
 *   value    – right-aligned value (e.g. amount); valueClass colors it
 *   onClick  – makes the row tappable
 *   trailing – optional extra node after the value (e.g. delete icon)
 */
export function ListRow({ leading, title, sub, value, valueClass = '', onClick, trailing, footer }) {
  return (
    <div
      className={`list-row${onClick ? ' list-row-tappable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="list-row-top">
        {leading != null && <div className="list-row-leading">{leading}</div>}
        <div className="list-row-main">
          <div className="list-row-title">{title}</div>
          {sub != null && <div className="list-row-sub">{sub}</div>}
        </div>
        {value != null && <div className={`list-row-value ${valueClass}`}>{value}</div>}
        {trailing}
      </div>
      {footer != null && <div className="list-row-footer">{footer}</div>}
    </div>
  );
}

/* ─── Swipe-to-action wrapper (touch) ───────────────────────────────────────
 * Wraps a row; swiping reveals an action set on either side. Phone-only —
 * desktop keeps explicit buttons.
 *   leftActions  – revealed by swiping the row LEFT  (R→L); panel on the right edge
 *   rightActions – revealed by swiping the row RIGHT (L→R); panel on the left edge
 * Each action: { key, label, icon, color: 'danger'|'warning'|'default'|<css>, onAction }.
 * Back-compat: `onDelete` is shorthand for a single danger left-action.
 */
const SWIPE_ACTION_W = 88;

function swipeActionClass(color) {
  if (color === 'danger') return 'swipe-action-danger';
  if (color === 'warning') return 'swipe-action-warning';
  if (color === 'edit') return 'swipe-action-edit';
  if (color === 'default' || !color) return 'swipe-action-default';
  return ''; // raw CSS color → applied via inline style
}

export function SwipeRow({ children, leftActions, rightActions, onDelete }) {
  const left = leftActions && leftActions.length
    ? leftActions
    : (onDelete ? [{ key: 'delete', label: 'Delete', icon: <Trash2 size={16} />, color: 'danger', onAction: onDelete }] : []);
  const right = rightActions || [];

  const leftW = left.length * SWIPE_ACTION_W;
  const rightW = right.length * SWIPE_ACTION_W;

  const [tx, setTx] = useState(0);          // current translateX (− reveals left actions, + reveals right)
  const [dragging, setDragging] = useState(false);
  const txRef = useRef(0);
  const start = useRef({ x: 0, y: 0, base: 0, locked: null });

  function onTouchStart(e) {
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY, base: txRef.current, locked: null };
  }
  function onTouchMove(e) {
    const t = e.touches[0];
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;
    if (start.current.locked === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      start.current.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (start.current.locked !== 'h') return;
    setDragging(true);
    const next = Math.max(-leftW, Math.min(rightW, start.current.base + dx));
    txRef.current = next;
    setTx(next);
  }
  function onTouchEnd() {
    setDragging(false);
    let v = 0;
    if (txRef.current <= -leftW / 2 && leftW) v = -leftW;
    else if (txRef.current >= rightW / 2 && rightW) v = rightW;
    txRef.current = v;
    setTx(v);
  }
  function close() { txRef.current = 0; setTx(0); }

  function renderActions(actions, side) {
    if (!actions.length) return null;
    return (
      <div className={`swipe-actions swipe-actions-${side}`}>
        {actions.map(a => (
          <button
            key={a.key || a.label}
            className={`swipe-action ${swipeActionClass(a.color)}`}
            style={swipeActionClass(a.color) ? undefined : { background: a.color }}
            tabIndex={-1}
            aria-label={a.label}
            onClick={() => { close(); a.onAction(); }}
          >
            {a.icon} {a.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`swipe-row${dragging ? ' swiping' : ''}`}>
      {renderActions(right, 'start')}
      {renderActions(left, 'end')}
      <div
        className="swipe-row-content"
        style={{ transform: `translateX(${tx}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}

/* ─── CardList (declarative mobile table) ────────────────────────────────────
 * One shared component every phone "table" uses, so tap/swipe behave the same
 * everywhere. Describe it with columns + a tap callback + per-side slide actions:
 *
 *   <CardList
 *     rows={items} rowKey={r => r.id}
 *     columns={[{ slot:'leading'|'title'|'sub'|'value'|'trailing', render:r=>node,
 *                 valueClass:r=>'expense-text' }]}
 *     onTap={r => openEdit(r)}
 *     slideLeft={[{ label, icon, color, onAction:r=>…, confirm:{title,message,danger} }]}
 *     slideRight={[…]} />
 *
 * slideLeft = revealed by swiping left (R→L, e.g. Delete);
 * slideRight = revealed by swiping right (L→R, e.g. Archive).
 * An action with `confirm` routes through ConfirmDialog (owned here) before firing.
 */
export function CardList({ rows, rowKey, columns = [], onTap, slideLeft = [], slideRight = [], className = '' }) {
  const [confirm, setConfirm] = useState(null); // { action, row }

  const bySlot = slot => columns.filter(c => c.slot === slot);
  const leadingCol = bySlot('leading')[0];
  const titleCols = bySlot('title');
  const subCols = bySlot('sub');
  const valueCol = bySlot('value')[0];
  const trailingCols = bySlot('trailing');
  const footerCol = bySlot('footer')[0];

  const joinNodes = (cols, row) =>
    cols.length ? <>{cols.map((c, i) => <React.Fragment key={i}>{c.render(row)}</React.Fragment>)}</> : undefined;

  function buildActions(list, row) {
    return list.map(a => ({
      key: a.key || a.label,
      label: a.label,
      icon: a.icon,
      color: a.color,
      onAction: () => (a.confirm ? setConfirm({ action: a, row }) : a.onAction(row)),
    }));
  }

  return (
    <div className={`card-list ${className}`.trim()}>
      {rows.map(row => {
        const key = rowKey ? rowKey(row) : row.id;
        const left = buildActions(slideLeft, row);
        const right = buildActions(slideRight, row);
        const vc = valueCol && valueCol.valueClass
          ? (typeof valueCol.valueClass === 'function' ? valueCol.valueClass(row) : valueCol.valueClass)
          : '';
        const rowNode = (
          <ListRow
            onClick={onTap ? () => onTap(row) : undefined}
            leading={leadingCol ? leadingCol.render(row) : undefined}
            title={titleCols.length === 1 ? titleCols[0].render(row) : joinNodes(titleCols, row)}
            sub={joinNodes(subCols, row)}
            value={valueCol ? valueCol.render(row) : undefined}
            valueClass={vc}
            trailing={joinNodes(trailingCols, row)}
            footer={footerCol ? footerCol.render(row) : undefined}
          />
        );
        return (left.length || right.length)
          ? <SwipeRow key={key} leftActions={left} rightActions={right}>{rowNode}</SwipeRow>
          : <React.Fragment key={key}>{rowNode}</React.Fragment>;
      })}
      {confirm && (
        <ConfirmDialog
          title={confirm.action.confirm.title}
          message={confirm.action.confirm.message}
          danger={confirm.action.confirm.danger}
          onConfirm={() => { confirm.action.onAction(confirm.row); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/* ─── Period Selector ─── */
export function PeriodSelector({ period, onChange, customFrom, customTo, onCustomFrom, onCustomTo }) {
  const periods = [
    { id: 'month', label: 'This Month' },
    { id: 'ytd',   label: 'YTD' },
    { id: 'year',  label: 'Full Year' },
    { id: 'prev',  label: 'Prev Month' },
    { id: 'custom',label: 'Custom' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
      <div className="period-bar" style={{ marginBottom: 0 }}>
        {periods.map(p => (
          <button key={p.id} className={`period-btn${period === p.id ? ' active' : ''}`} onClick={() => onChange(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
      {period === 'custom' && (
        <div className="flex period-custom" style={{ gap: 6 }}>
          <input type="date" value={customFrom} onChange={e => onCustomFrom(e.target.value)} style={{ width: 140 }} />
          <span className="muted">→</span>
          <input type="date" value={customTo} onChange={e => onCustomTo(e.target.value)} style={{ width: 140 }} />
        </div>
      )}
    </div>
  );
}

/* ─── Searchable Select ─── */
/**
 * options: Array<{ value: string, label: string, secondary?: string }>
 * Renders an <input> that filters options; selecting one calls onChange(value).
 */
export function SearchableSelect({
  value, onChange, options,
  placeholder = 'Search…', className = '', style = {},
  onCreateOption,          // (queryString) => void — called when user picks the "create" item
  createLabel = 'Add as new option', // suffix in "+ Add "{query}" {createLabel}"
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef();
  const listRef  = useRef();

  const selected = options.find(o => o.value === value);

  // When open: filter by query; when closed: show full list (hidden anyway)
  const filtered = query
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        o.value.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  // Show create option when there's a typed query that doesn't exactly match any label
  const showCreate = !!(
    onCreateOption &&
    query.trim().length > 1 &&
    !filtered.some(o => o.label.toLowerCase() === query.trim().toLowerCase())
  );

  function handleFocus() {
    setQuery('');
    setOpen(true);
  }

  function handleBlur(e) {
    // Delay so onMouseDown on a list item fires first
    setTimeout(() => {
      if (!listRef.current?.contains(document.activeElement)) {
        setOpen(false);
        setQuery('');
      }
    }, 150);
  }

  function pick(val) {
    onChange(val);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur(); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) { pick(filtered[0].value); }
      else if (showCreate) { onCreateOption(query.trim()); setOpen(false); setQuery(''); inputRef.current?.blur(); }
    }
  }

  const displayValue = open ? query : (selected?.label || '');

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        ref={inputRef}
        value={displayValue}
        onChange={e => setQuery(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={open ? 'Type to search…' : (placeholder || 'Select…')}
        className={className}
        style={{ width: '100%', cursor: 'pointer' }}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <div
          ref={listRef}
          className="ss-dropdown"
          style={{
            position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0,
            zIndex: 9999,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            maxHeight: 260,
            overflowY: 'auto',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {filtered.length === 0 && !showCreate && (
            <div style={{ padding: '10px 14px', color: 'var(--text-tertiary)', fontSize: 13 }}>
              No results for "{query}"
            </div>
          )}
          {filtered.slice(0, 120).map(o => (
            <div
              key={o.value}
              className={`ss-option${o.value === value ? ' ss-selected' : ''}`}
              onMouseDown={e => { e.preventDefault(); pick(o.value); }}
            >
              <span style={{ flex: 1 }}>{o.label}</span>
              {o.secondary && (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                  {o.secondary}
                </span>
              )}
            </div>
          ))}
          {showCreate && (
            <div
              className="ss-create"
              style={{ borderTop: filtered.length > 0 ? '1px solid var(--border)' : 'none' }}
              onMouseDown={e => { e.preventDefault(); onCreateOption(query.trim()); setOpen(false); setQuery(''); inputRef.current?.blur(); }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
              <span>Add "{query.trim()}" {createLabel}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Category Select ───────────────────────────────────────────────────────
 * Searchable category picker that includes yearly expenses and lets the user
 * create a new yearly expense by typing a free-text name that has no match.
 *
 * Props:
 *   value             – currently selected category/yearly-expense id
 *   onChange          – (id) => void
 *   categories        – full categories array from context
 *   yearlyExpenses    – yearlyExpenses array from context (default [])
 *   type              – 'income' | 'expense'  (default 'expense')
 *   addYearlyExpense  – optional (name, ...) => id; enables free-text creation
 *   showToast         – optional toast fn for creation feedback
 *   className, placeholder – passed through to SearchableSelect
 */
export function CategorySelect({
  value, onChange,
  categories, yearlyExpenses = [],
  type = 'expense',
  addYearlyExpense,
  showToast,
  className = '',
  placeholder = 'Select category…',
}) {
  const filtered = categories.filter(c => c.type === type);
  const options = [
    ...filtered.map(c => ({ value: c.id, label: c.name })),
    ...(type === 'expense'
      ? yearlyExpenses.map(y => ({ value: y.id, label: `📅 ${y.name}` }))
      : []),
  ];

  function handleCreate(name) {
    if (!addYearlyExpense) return;
    const id = addYearlyExpense({ name, amount: 0, month: null, frequency: 'annual', notes: '' });
    onChange(id);
    showToast?.(`"${name}" added as yearly expense`);
  }

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      className={className}
      onCreateOption={addYearlyExpense ? handleCreate : undefined}
      createLabel="as yearly expense"
    />
  );
}

/* ─── Theme Toggle ─── */
export function ThemeToggle({ theme, onToggle }) {
  const isDark = theme === 'dark';
  return (
    <button
      className={`toggle${isDark ? ' on' : ''}`}
      onClick={onToggle}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <span className="toggle-icon">
        {isDark ? <Moon size={8} strokeWidth={2.5} /> : <Sun size={8} strokeWidth={2.5} />}
      </span>
    </button>
  );
}

/**
 * Shows the current file-save state in the nav bar.
 * Clicking it triggers a save.
 */
export function FileSaveChip({ fileName, hasUnsavedChanges, onSave }) {
  if (!fileName) return null;
  const unsaved = hasUnsavedChanges;
  return (
    <button
      className={`file-save-chip${unsaved ? ' unsaved' : ' saved'}`}
      onClick={onSave}
      title={unsaved ? `Unsaved changes — click or Ctrl+S to save to ${fileName}` : `Saved to ${fileName}`}
      aria-label={unsaved ? 'Save file' : 'File saved'}
    >
      <span className="file-save-chip-dot" />
      <span className="file-save-chip-name">{fileName}</span>
      {unsaved
        ? <><Save size={10} /><span>unsaved</span></>
        : <Check size={10} />
      }
    </button>
  );
}

/**
 * Banner shown below the nav when the linked file was changed on disk by another process.
 */
export function ExternalChangeBar({ fileName, onReload, onDismiss }) {
  return (
    <div className="external-change-bar" role="alert">
      <AlertTriangle size={14} />
      <span><strong>{fileName}</strong> was changed on disk.</span>
      <button className="btn btn-sm" onClick={onReload}>
        <RefreshCw size={12} /> Reload
      </button>
      <button className="btn btn-ghost btn-sm" onClick={onDismiss}>Ignore</button>
    </div>
  );
}
